import { FormEvent, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import {
  DashValidation,
  ServerErrors,
  isNameLengthValid,
  or,
} from 'dash-common';
import {
  Button,
  Flex,
  Form,
  FormErrorTypes,
  InputAutocomplete,
  Modal,
  ModalContext,
  Text,
  ViewerContext,
  toast,
  useFormState,
  useFormValidation,
  useServerResponseTranslation,
} from 'dash-ui';
import {
  CurrentBrokerHasPortfoliosDocument,
  GetAllPortfolioFoldersDocument,
  GetPortfolioFolderItemsDocument,
  GetPortfoliosTreeDocument,
  GetPortfoliosDocument,
  useAddPropertyToPortfolioMutation,
  useCreatePortfolioFolderMutation,
  useGetAllPortfolioFoldersQuery,
  useGetPropertyByIdQuery,
} from 'gql/generated.graphql';
import { GTMEventsEnum } from 'common/google-tag-manager/constants';
import {
  createGTMEvent,
  getGoogleTagManagerUserId,
} from 'common/google-tag-manager/helpers';
import {
  buildAddress,
  buildBuiltRange,
  buildRangeString,
} from 'common/helpers';

enum FormFields {
  FolderName = 'folderName',
  CreatedFolderId = 'createdFolderId',
}

const AddPropertyToPortfolioModal = () => {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'pages']);
  const { account } = useContext(ViewerContext);
  const { isOpenCurrent, toggleModal, payload } = useContext(ModalContext);
  const { data: propertyData } =
    useGetPropertyByIdQuery({
      variables: { propertyId: payload?.propertyId },
    }) || {};
  const isOpen = isOpenCurrent['AddPropertyToPortfolio'];
  const [formState, [setFolderName, setCreatedFolderId]] = useFormState([
    FormFields.FolderName,
    FormFields.CreatedFolderId,
  ]);
  const [formValidation, , [setFolderNameError]] = useFormValidation([
    FormFields.FolderName,
  ]);
  const formRef = useRef<HTMLFormElement | null>(null);

  const [createPortfolioFolder] = useCreatePortfolioFolderMutation({
    refetchQueries: [
      GetPortfoliosDocument,
      GetAllPortfolioFoldersDocument,
      CurrentBrokerHasPortfoliosDocument,
    ],
  });

  const { data } = useGetAllPortfolioFoldersQuery({
    fetchPolicy: 'network-only',
  });
  const portfolioFolders = useMemo(
    () => data?.getAllPortfolioFolders || [],
    [data]
  );
  const isFolderExist = !!portfolioFolders.find(
    (item) => item.name === formState.folderName
  );

  const [addPropertyToPortfolio, { loading: isLoading }] =
    useAddPropertyToPortfolioMutation();
  const translateErrorMessage = useServerResponseTranslation();

  const closeModal = () => {
    toggleModal(false, 'AddPropertyToPortfolio');
    setFolderName('');
    setCreatedFolderId('');
    setFolderNameError(null);
  };

  const handleInputValue = (value: string) => {
    setFolderName(value);
    if (!value) {
      return setFolderNameError({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.nameBlank'),
      });
    }
    if (!isNameLengthValid(value as string)) {
      return setFolderNameError({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.maxCharacterLimit', {
          number: DashValidation.MaxNameLength.toString(),
        }),
      });
    }

    if (value.includes("/")) {
      return setFolderNameError({
        type: FormErrorTypes.Invalid,
        message: t("forms:messages.invalidCharacters"),
      });
    }
    setFolderNameError(null);
  };

  const onFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const portfolioFolderId = isFolderExist
      ? portfolioFolders.find(({ name }) => name === formState.folderName)?._id
      : formState.createdFolderId;

    addPropertyToPortfolio({
      variables: {
        propertyId: payload.propertyId,
        portfolioFolderId,
      },
      refetchQueries: [
        CurrentBrokerHasPortfoliosDocument,
        {
          query: GetPortfoliosDocument,
          variables: {
            pagination: {
              limit: 10,
            },
          },
          fetchPolicy: 'network-only',
        },
        {
          query: GetPortfolioFolderItemsDocument,
          variables: {
            portfolioFolderId,
          },
          fetchPolicy: 'network-only',
        },
        {
          query: GetPortfoliosTreeDocument,
          fetchPolicy: 'network-only',
        },
      ],
      onCompleted: () => {
        closeModal();
        if (account && propertyData) {
          const { getPropertyById } = propertyData;
          createGTMEvent({
            dataLayer: {
              event: GTMEventsEnum.PROPERTY_ADDED_TO_PORTFOLIO,
              user_id: getGoogleTagManagerUserId(account.email),
              property_name: getPropertyById.name,
              price_range: buildRangeString(getPropertyById.priceRange),
              property_type: getPropertyById.buildingTypes,
              number_of_bedrooms: buildRangeString(
                getPropertyById.averageBedroomsNumberValue
              ),
              number_of_bathrooms: buildRangeString(
                getPropertyById.averageBathroomsNumberValue
              ),
              features_and_amenities: getPropertyById.amenityNames
                ?.map(({ name }: { name: string }) => name.trim())
                .join(', '),
              number_of_units: getPropertyById.availableUnitsNumber,
              appeared_date: getPropertyById.createdAt,
              completion_year: getPropertyById.yearBuilt,
              scheduled_construction: buildBuiltRange({
                startDate: getPropertyById.constructionStartDate,
                endDate:
                  getPropertyById.completionDate ||
                  `${getPropertyById.yearBuilt}`,
              }),
              project_status: getPropertyById.projectStatus,
              year_built: buildBuiltRange({
                startDate: getPropertyById.constructionStartDate,
                endDate:
                  getPropertyById.completionDate ||
                  `${getPropertyById.yearBuilt}`,
                yearOnly: true,
              }),
              marketArea: getPropertyById.marketArea,
              address: buildAddress(getPropertyById.location.address),
            },
          });
        }
        toast.success(t('pages:portfoliosPage.toasts.propertyWasAdded'));
      },
      onError: (error) => {
        if (error.message === ServerErrors.PORTFOLIO_CONTAIN_FOLDER) {
          return toast.error(
            t('pages:portfoliosPage.toasts.propertyAlreadyAdded')
          );
        }
        toast.error(translateErrorMessage(error));
      },
    });
  };

  const createFolder = () => {
    if (formState.folderName.includes("/")) {
      return setFolderNameError({
        type: FormErrorTypes.Invalid,
        message: t("forms:messages.invalidCharacters"),
      });
    }
    if (!isFolderExist) {
      createPortfolioFolder({
        variables: {
          name: formState.folderName,
        },
        onCompleted: (data) => {
          setCreatedFolderId(data.createPortfolioFolder._id);
          toast.success(t('pages:portfoliosPage.toasts.portfolioIsCreated'));
        },
        onError: (error) => toast.error(translateErrorMessage(error)),
      });
    }
  };

  const foldersOptions = useMemo(() => {
    const portfolios = portfolioFolders.map((item) => {
      const isDisabled = item.portfolioPropertiesIds?.some(
        (id) => id === payload?.propertyId
      );

      return {
        value: item.name || '',
        label: item.name || '',
        isDisabled,
      };
    })
  
    const filteredPortfolios = portfolios.filter((portfolio) => {
      return portfolio.value !== 'Root'
    });

    return filteredPortfolios;
  },
  [portfolioFolders, payload?.propertyId]
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      title={t('pages:portfoliosPage.addPropertyToPortfolioModalTitle') || ''}
      submitButton={{
        title: t('common:labels.add'),
        isLoading,
        disabled: or(!isFolderExist, isLoading),
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{
        title: t('common:labels.cancel'),
        onClick: closeModal,
      }}
    >
      <Form ref={formRef} onSubmit={onFormSubmit}>
        <Text color={theme.colors.n80}>
          {t('pages:portfoliosPage.addPropertyToPortfolioModalText') || ''}
        </Text>
        <Form.Group mt={3} mb={1}>
          <Form.Label>{t('forms:placeholders.folderName')}</Form.Label>
          <Flex>
            <InputAutocomplete
              w="100%"
              maxWidth={[200, 300, 300, 300]}
              placeholder={t('forms:placeholders.enterFolderName') || ''}
              onChange={handleInputValue}
              options={foldersOptions}
              $errorType={formValidation.folderName?.type}
            />
            <Button
              p={2}
              minWidth={72}
              type="button"
              ml={2}
              variation="outline"
              onClick={createFolder}
              disabled={!formState.folderName || isFolderExist}
            >
              {t('common:labels.create')}
            </Button>
          </Flex>
          <Form.Feedback $errorType={formValidation.folderName?.type}>
            {formValidation.folderName?.message}
          </Form.Feedback>
        </Form.Group>
      </Form>
    </Modal>
  );
};

export default AddPropertyToPortfolioModal;
